/**
 * @wapcentral/config - adTestCatalog.ts
 *
 * Official platform-specific advertising test configuration catalog for WAPCentral.
 * Contains verified official test unit IDs, test mode methods, documentation links,
 * and support statuses across Google AdMob, AppLovin MAX, Meta, InMobi, and Unity Ads.
 */
/**
 * Official Google AdMob Sample Test IDs (Verified from Google Mobile Ads SDK Documentation).
 * Android: https://developers.google.com/admob/android/test-ads#sample_ad_units
 * iOS: https://developers.google.com/admob/ios/test-ads#sample_ad_units
 */
export const ADMOB_OFFICIAL_TEST_IDS = {
    android: {
        appId: 'ca-app-pub-3940256099942544~3347511713',
        bannerAdaptive: 'ca-app-pub-3940256099942544/9214589741',
        bannerFixed: 'ca-app-pub-3940256099942544/6300978111',
        native: 'ca-app-pub-3940256099942544/2247696110',
        interstitial: 'ca-app-pub-3940256099942544/1033173712',
        rewarded: 'ca-app-pub-3940256099942544/5224354917',
        appOpen: 'ca-app-pub-3940256099942544/9257395921',
        rewardedInterstitial: 'ca-app-pub-3940256099942544/5354046379',
    },
    ios: {
        appId: 'ca-app-pub-3940256099942544~1458002511',
        bannerAdaptive: 'ca-app-pub-3940256099942544/2435281174',
        bannerFixed: 'ca-app-pub-3940256099942544/2934735716',
        native: 'ca-app-pub-3940256099942544/3986624511',
        interstitial: 'ca-app-pub-3940256099942544/4411468910',
        rewarded: 'ca-app-pub-3940256099942544/1712485313',
        appOpen: 'ca-app-pub-3940256099942544/5575463023',
        rewardedInterstitial: 'ca-app-pub-3940256099942544/6978759866',
    },
};
/**
 * Complete Official Ad Testing Catalog grouped by provider and format.
 */
export const OFFICIAL_AD_TEST_CATALOG = [
    // ============================================================
    // AppLovin MAX (Sole Mediation Engine)
    // ============================================================
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner (Adaptive / Fixed)',
        testingMethod: 'MAX Test Mode / Mediation Debugger (AppLovinMAX.showMediationDebugger()) using app MAX Ad Unit ID',
        androidSampleId: undefined, // MAX does not provide fake universal IDs; uses real MAX Ad Unit with test mode
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device GAID/IDFA in AppLovin Dashboard > MAX > Test Mode or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'MAX Test Mode / Mediation Debugger using app MAX Native Ad Unit ID',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device in AppLovin Dashboard or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'MAX Test Mode / Mediation Debugger using app MAX Interstitial Ad Unit ID',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device in AppLovin Dashboard or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'MAX Test Mode / Mediation Debugger using app MAX Rewarded Ad Unit ID',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device in AppLovin Dashboard or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'MAX Test Mode / Mediation Debugger using app MAX App Open Ad Unit ID',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device in AppLovin Dashboard or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    {
        providerId: 'applovin',
        providerName: 'AppLovin MAX',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'MAX Test Mode / Mediation Debugger using app MAX Rewarded Interstitial Ad Unit ID',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires registered test device in AppLovin Dashboard or Mediation Debugger',
        documentationUrl: 'https://dash.applovin.com/documentation/mediation/test-mode',
        isUniversalId: false,
    },
    // ============================================================
    // Google AdMob
    // ============================================================
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner — adaptive',
        testingMethod: 'Official Google Mobile Ads Sample Ad Unit (Automatic test ad fill)',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.bannerAdaptive,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.bannerAdaptive,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for adaptive banners',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'banner_fixed',
        formatLabel: 'Banner — fixed size',
        testingMethod: 'Official Google Mobile Ads Sample Ad Unit (320x50 standard banner)',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.bannerFixed,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.bannerFixed,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for fixed-size banners',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'Official Google Mobile Ads Sample Native Ad Unit',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.native,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.native,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for native advanced ads',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'Official Google Mobile Ads Sample Interstitial Ad Unit',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.interstitial,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.interstitial,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for full-screen interstitials',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'Official Google Mobile Ads Sample Rewarded Video Ad Unit',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.rewarded,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.rewarded,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for rewarded video ads',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'Official Google Mobile Ads Sample App Open Ad Unit',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.appOpen,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.appOpen,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for app launch ads',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    {
        providerId: 'admob',
        providerName: 'Google AdMob',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'Official Google Mobile Ads Sample Rewarded Interstitial Ad Unit',
        androidSampleId: ADMOB_OFFICIAL_TEST_IDS.android.rewardedInterstitial,
        iosSampleId: ADMOB_OFFICIAL_TEST_IDS.ios.rewardedInterstitial,
        defaultStatus: 'configured',
        statusDescription: 'Official public test ad unit active for rewarded interstitial ads',
        documentationUrl: 'https://developers.google.com/admob/android/test-ads#sample_ad_units',
        isUniversalId: true,
    },
    // ============================================================
    // Meta Audience Network
    // ============================================================
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner',
        testingMethod: 'Test Device registration via FBAdSettings.addTestDevice() or test placement prefix',
        androidSampleId: 'IMG_16_9_APP_INSTALL#YOUR_PLACEMENT_ID',
        iosSampleId: 'IMG_16_9_APP_INSTALL#YOUR_PLACEMENT_ID',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires test device registration or placement created in Meta Monetization Manager',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network/guides/test-ads',
        isUniversalId: false,
    },
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'Test Device registration or test placement prefix',
        androidSampleId: 'IMG_16_9_APP_INSTALL#YOUR_PLACEMENT_ID',
        iosSampleId: 'IMG_16_9_APP_INSTALL#YOUR_PLACEMENT_ID',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires test device registration or placement in Meta Monetization Manager',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network/guides/test-ads',
        isUniversalId: false,
    },
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'Test Device registration or video test placement prefix',
        androidSampleId: 'VID_HD_9_16_39S_APP_INSTALL#YOUR_PLACEMENT_ID',
        iosSampleId: 'VID_HD_9_16_39S_APP_INSTALL#YOUR_PLACEMENT_ID',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires test device registration in Meta Monetization Manager',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network/guides/test-ads',
        isUniversalId: false,
    },
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'Test Device registration or video test placement prefix',
        androidSampleId: 'VID_HD_9_16_39S_APP_INSTALL#YOUR_PLACEMENT_ID',
        iosSampleId: 'VID_HD_9_16_39S_APP_INSTALL#YOUR_PLACEMENT_ID',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires test device registration in Meta Monetization Manager',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network/guides/test-ads',
        isUniversalId: false,
    },
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'Not supported by Meta Audience Network',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'Meta Audience Network does not offer App Open ad format',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network',
        isUniversalId: false,
    },
    {
        providerId: 'meta',
        providerName: 'Meta Audience Network',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'Not supported by Meta Audience Network',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'Meta Audience Network does not offer Rewarded Interstitial format',
        documentationUrl: 'https://developers.facebook.com/docs/audience-network',
        isUniversalId: false,
    },
    // ============================================================
    // InMobi
    // ============================================================
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner',
        testingMethod: 'InMobi Portal Test Mode (Toggle Test Mode: ON for placement) or MAX Mediation Debugger',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'requires_provider_setup',
        statusDescription: 'Requires Account ID and Placement ID registered in InMobi Portal with Test Mode enabled',
        documentationUrl: 'https://support.inmobi.com/monetize/integration-guides/android-sdk/testing-your-integration/',
        isUniversalId: false,
    },
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'InMobi Portal Test Mode (Toggle Test Mode: ON for placement)',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'requires_provider_setup',
        statusDescription: 'Requires Placement ID registered in InMobi Portal with Test Mode enabled',
        documentationUrl: 'https://support.inmobi.com/monetize/integration-guides/android-sdk/testing-your-integration/',
        isUniversalId: false,
    },
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'InMobi Portal Test Mode (Toggle Test Mode: ON for placement)',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'requires_provider_setup',
        statusDescription: 'Requires Placement ID registered in InMobi Portal with Test Mode enabled',
        documentationUrl: 'https://support.inmobi.com/monetize/integration-guides/android-sdk/testing-your-integration/',
        isUniversalId: false,
    },
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'InMobi Portal Test Mode (Toggle Test Mode: ON for placement)',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'requires_provider_setup',
        statusDescription: 'Requires Placement ID registered in InMobi Portal with Test Mode enabled',
        documentationUrl: 'https://support.inmobi.com/monetize/integration-guides/android-sdk/testing-your-integration/',
        isUniversalId: false,
    },
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'Not supported by InMobi',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'InMobi does not support App Open format',
        documentationUrl: 'https://support.inmobi.com',
        isUniversalId: false,
    },
    {
        providerId: 'inmobi',
        providerName: 'InMobi',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'Not supported by InMobi',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'InMobi does not support Rewarded Interstitial format',
        documentationUrl: 'https://support.inmobi.com',
        isUniversalId: false,
    },
    // ============================================================
    // Unity Ads
    // ============================================================
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner',
        testingMethod: 'Unity Dashboard Test Mode (Project Settings > Monetization > Testing) or SDK init testMode: true',
        androidSampleId: 'Banner_Android',
        iosSampleId: 'Banner_iOS',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires Unity Game ID from Unity Dashboard with Test Mode enabled',
        documentationUrl: 'https://docs.unity.com/ads/en-us/manual/Testing',
        isUniversalId: true,
    },
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'Not supported by Unity Ads',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'Unity Ads does not support native ads format',
        documentationUrl: 'https://docs.unity.com/ads',
        isUniversalId: false,
    },
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'Unity Dashboard Test Mode with default placement name',
        androidSampleId: 'Interstitial_Android',
        iosSampleId: 'Interstitial_iOS',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires Unity Game ID from Unity Dashboard with Test Mode enabled',
        documentationUrl: 'https://docs.unity.com/ads/en-us/manual/Testing',
        isUniversalId: true,
    },
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'Unity Dashboard Test Mode with default placement name',
        androidSampleId: 'Rewarded_Android',
        iosSampleId: 'Rewarded_iOS',
        defaultStatus: 'test_mode_required',
        statusDescription: 'Requires Unity Game ID from Unity Dashboard with Test Mode enabled',
        documentationUrl: 'https://docs.unity.com/ads/en-us/manual/Testing',
        isUniversalId: true,
    },
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'Not supported by Unity Ads',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'Unity Ads does not support App Open format',
        documentationUrl: 'https://docs.unity.com/ads',
        isUniversalId: false,
    },
    {
        providerId: 'unity',
        providerName: 'Unity Ads',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'Not supported by Unity Ads',
        androidSampleId: undefined,
        iosSampleId: undefined,
        defaultStatus: 'not_supported',
        statusDescription: 'Unity Ads does not support Rewarded Interstitial format',
        documentationUrl: 'https://docs.unity.com/ads',
        isUniversalId: false,
    },
    // ============================================================
    // WebAppyPie Custom Ads
    // ============================================================
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'banner_adaptive',
        formatLabel: 'Banner',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_banner',
        iosSampleId: 'custom_inhouse_banner',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo creative served on-device with zero external SDK dependencies',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'native',
        formatLabel: 'Native',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_native',
        iosSampleId: 'custom_inhouse_native',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo creative served on-device',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'interstitial',
        formatLabel: 'Interstitial',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_interstitial',
        iosSampleId: 'custom_inhouse_interstitial',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo interstitial served on-device',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'rewarded',
        formatLabel: 'Rewarded',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_rewarded',
        iosSampleId: 'custom_inhouse_rewarded',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo rewarded dialog served on-device',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'app_open',
        formatLabel: 'App Open',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_app_open',
        iosSampleId: 'custom_inhouse_app_open',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo app launch overlay served on-device',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
    {
        providerId: 'custom_ads',
        providerName: 'WebAppyPie Custom Ads',
        formatKey: 'rewarded_interstitial',
        formatLabel: 'Rewarded Interstitial',
        testingMethod: 'Internal house ads fallback creative configured in WAPCentral',
        androidSampleId: 'custom_inhouse_rewarded_interstitial',
        iosSampleId: 'custom_inhouse_rewarded_interstitial',
        defaultStatus: 'configured',
        statusDescription: 'Active house promo rewarded interstitial served on-device',
        documentationUrl: 'https://wapcentral.webappypie.com/ads',
        isUniversalId: true,
    },
];
//# sourceMappingURL=adTestCatalog.js.map